/* eslint-disable default-case */
import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiCheck } from 'react-icons/fi';
import Icon from '../../components/icons/Icon';
import { LoginPageState } from '../../types/navigation';
import { authenticationService } from '../../services';
import ResponsePageWrapper from '../../components/layouts/ResponsePageWrapper';
import { InviteType } from '../../types/invites';
import Button from '../../components/misc/Button';
import useAuth from '../../hooks/useAuth';
import { CommunityOverview } from '../../components/misc/CommunityOverview';

export default function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const {
    email,
    invitationId,
    communityInvitationId,
    inviteType,
    personalLinkId,
    eventId,
    hideBackButton = false,
    networkInvite,
  }: LoginPageState = location.state || {};
  const auth = useAuth();

  const [emailInput, setEmailInput] = useState<string>(email || '');

  useEffect(() => {
    if (auth) navigate('/');
  }, []);

  const handleSubmit = async () => {
    setEmailSending(true);
    setEmailSent(false);
    await authenticationService
      .generateMagicLink(emailInput, invitationId, communityInvitationId, inviteType, personalLinkId, eventId)
      .then(() => {
        setEmailSending(false);
        setEmailSent(true);
        toast.success(t('toast.success.login'));
      })
      .catch((error) => {
        setEmailSending(false);
        setEmailSent(false);
        if (error.response?.status === 404) {
          toast.error(t('toast.server.inexistantUser'));
        }
      });
  };

  const navigateBack = () => {
    if (!location.state) return;
    if (personalLinkId) {
      navigate(`/personalLink?linkId=${personalLinkId}`);
    }

    switch (inviteType) {
      case InviteType.USER:
        const { invite }: LoginPageState = location.state;
        navigate(`/invite?invitationId=${invite?.id}`, { state: location.state });
        return;
      case InviteType.BUSINESS:
        navigate(`/moments/invite?invitationId=${invitationId}`, { state: location.state });
        return;
      case InviteType.LOCAL_CONTACT:
        navigate(`/moments/local-invite?invitationId=${invitationId}`, { state: location.state });
        return;
      case InviteType.CRM_USER_INVITE:
        navigate(`/crm/invite?invitationId=${invitationId}`, { state: location.state });
        return;
      case InviteType.PERSONAL:
        navigate(`/personal/invite?invitationId=${invitationId}`, { state: location.state });
        break;
      case InviteType.EVENT:
        navigate(`/event?id=${invitationId}`, { state: location.state });
        break;
      case InviteType.DATE_PICKER:
        navigate(`/date-picker?id=${invitationId}`, { state: location.state });
    }
  };

  return (
    <ResponsePageWrapper hideSidePanel={!networkInvite}>
      {networkInvite && (
        <ResponsePageWrapper.SidePanel>
          <CommunityOverview invite={networkInvite} />
        </ResponsePageWrapper.SidePanel>
      )}

      <div className="flex w-full flex-col">
        <h1 className="text-center text-3xl font-semibold">{t('page.login.title')}</h1>
        <p className="mt-4 text-center text-sm">{t('page.login.message')}</p>
        <div className="mt-6 flex w-full flex-col">
          <label className="focus:border-3 mb-1 pr-2 text-sm focus:ring-0 focus:ring-transparent">
            {t('form.email.email')}
          </label>
          <div className="relative">
            <input
              className={twMerge(
                'focus:border-3 h-10 w-full rounded-lg border px-2 pr-10',
                networkInvite ? '' : '!border-primary !bg-secondary-50',
                emailSent ? 'text-primary/60' : 'text-primary',
              )}
              name="email"
              type="text"
              disabled={emailSending}
              value={emailInput}
              placeholder={t('form.email.placeholder')}
              onChange={(e) => {
                setEmailInput(e.target.value);
                setEmailSent(false);
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            />
            {emailSent && (
              <div
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-primary text-secondary"
                aria-hidden>
                <Icon icon={FiCheck} className="size-5" />
              </div>
            )}
          </div>
        </div>
        {!hideBackButton && (
          <div className="flex justify-end text-primary">
            {location.state && (
              <span className="text-sm">
                {t('page.login.noAccount')}{' '}
                <strong className="cursor-pointer border-b border-black" onClick={() => navigateBack()}>
                  {t('page.login.register')}
                </strong>
              </span>
            )}
          </div>
        )}
        <div className="mt-8 flex justify-center">
          <Button variant="primary" disabled={emailSent || emailSending} onClick={handleSubmit}>
            {t('page.login.submit')}
          </Button>
        </div>
      </div>
    </ResponsePageWrapper>
  );
}
